// 型号表单弹窗组件
import React, { useState } from 'react';
import { X, Save } from 'lucide-react';
import type { ProductModel } from '@/poster/types';

interface ModelFormModalProps {
  model: ProductModel | null;
  productId: string;
  onClose: () => void;
  onSave: (data: Partial<ProductModel>) => Promise<void>;
}

export function ModelFormModal({
  model,
  productId: _productId,
  onClose,
  onSave,
}: ModelFormModalProps) {
  // productId 由父组件传入，用于创建新型号时的关联
  void _productId;
  const [formData, setFormData] = useState<Partial<ProductModel>>({
    model_name: model?.model_name || '',
    pitch: model?.pitch,
    cabinet_size: model?.cabinet_size || '',
    weight: model?.weight || '',
    power_consumption: model?.power_consumption || '',
    size_option: model?.size_option || '',
    is_default: model?.is_default || false,
  });
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.model_name) return;
    
    setIsSaving(true);
    try {
      await onSave(formData);
    } catch (error) {
      console.error('Error saving model:', error);
      alert('保存失败');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-lg overflow-hidden shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white">
          <h2 className="text-lg font-semibold text-gray-900">
            {model ? '编辑型号' : '添加型号'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            {/* Model Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                型号名称 *
              </label>
              <input
                type="text"
                value={formData.model_name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, model_name: e.target.value }))
                }
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="如: P0.9"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Pitch */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  像素间距 (mm)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.pitch || ''}
                  onChange={(e) =>
                    setFormData((prev) => ({ 
                      ...prev, 
                      pitch: e.target.value ? parseFloat(e.target.value) : undefined 
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="如: 0.9"
                />
              </div>

              {/* Cabinet Size */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  箱体尺寸
                </label>
                <input
                  type="text"
                  value={formData.cabinet_size}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, cabinet_size: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="如: 600×337.5mm"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Weight */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  重量
                </label>
                <input
                  type="text"
                  value={formData.weight}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, weight: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="如: 8.5kg"
                />
              </div>

              {/* Power Consumption */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  功耗
                </label>
                <input
                  type="text"
                  value={formData.power_consumption}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, power_consumption: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="如: 150W/㎡"
                />
              </div>
            </div>

            {/* Size Option */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                尺寸选项
              </label>
              <input
                type="text"
                value={formData.size_option}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, size_option: e.target.value }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder='如: 135" / 162"'
              />
            </div>

            {/* Is Default */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_default_model"
                checked={formData.is_default}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, is_default: e.target.checked }))
                }
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="is_default_model" className="text-sm text-gray-700">
                设为默认型号
              </label>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={isSaving || !formData.model_name}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {isSaving ? '保存中...' : '保存'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
